import * as http from 'node:http';

// Fixed port for the OAuth redirect URI — must match what's registered in WorkOS AuthKit.
export const OAUTH_CALLBACK_PORT = 3927;

const DEFAULT_AUTHKIT_DOMAIN = 'https://engaging-tip-62.authkit.app';
const DEFAULT_AUTHKIT_CLIENT_ID = 'client_01KSMZJ6YN7VAKTP2BW41V56SZ';

export function getAuthKitDomain(): string {
  return process.env['REKOR_AUTHKIT_DOMAIN'] || DEFAULT_AUTHKIT_DOMAIN;
}

export function getAuthKitClientId(): string {
  return process.env['REKOR_AUTHKIT_CLIENT_ID'] || DEFAULT_AUTHKIT_CLIENT_ID;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

// Decode a JWT's `iss` claim WITHOUT verifying its signature. Local diagnostics only (the
// cross-environment-token warning below) — never an auth decision; the backend remains the
// authority. Returns undefined for a non-JWT (e.g. a rec_ token), a malformed token, or a
// missing/non-string `iss`, so callers fail open and let the API surface a real error.
export function decodeUnverifiedIssuer(token: string): string | undefined {
  const segments = token.split('.');
  if (segments.length !== 3) return undefined;
  try {
    const payload = JSON.parse(Buffer.from(segments[1]!, 'base64url').toString('utf-8')) as { iss?: unknown };
    return typeof payload.iss === 'string' ? payload.iss : undefined;
  } catch {
    return undefined;
  }
}

// WorkOS emits two legitimate `iss` forms for the SAME tenant: the AuthKit domain
// (`https://<env>.authkit.app`) and the User-Management canonical form
// (`https://api.workos.com/user_management/<client_id>`). A token from the configured
// environment matches one of these; anything else is a different WorkOS environment/client —
// e.g. a stale login left in the keychain after switching environments — which the backend
// rejects as "issuer not recognized". Trailing slashes are normalized so a `iss` with one
// can't false-trigger.
export function issuerMatchesConfiguredEnv(iss: string, authkitDomain: string, clientId: string): boolean {
  const normalized = iss.replace(/\/+$/, '');
  if (normalized === authkitDomain.replace(/\/+$/, '')) return true;
  if (normalized === `https://api.workos.com/user_management/${clientId}`) return true;
  return false;
}

export function expiresInToIso(expiresInSeconds: number): string {
  return new Date(Date.now() + expiresInSeconds * 1000).toISOString();
}

// AuthKit only returns a refresh_token when the app is configured to issue them (offline_access).
// Without one, the short-lived access token can't be silently renewed — the session dies within
// minutes and every command fails until the next `rekor login`. Surface that at login time rather
// than letting the user discover it via a confusing mid-session auth failure.
export function refreshTokenMissingWarning(refreshToken: string | undefined): string | null {
  if (refreshToken) return null;
  return (
    'Signed in, but no refresh token was issued — this session will expire shortly and you will ' +
    'need to run `rekor login` again. Enable refresh tokens (offline_access) on the WorkOS AuthKit ' +
    'application to keep CLI sessions alive.'
  );
}

function asTokenResponse(value: unknown): TokenResponse {
  if (typeof value !== 'object' || value === null) {
    throw new Error('AuthKit returned a non-object token payload');
  }
  const v = value as Partial<TokenResponse>;
  if (typeof v.access_token !== 'string' || typeof v.expires_in !== 'number') {
    throw new Error('AuthKit token payload missing required fields');
  }
  const out: TokenResponse = { access_token: v.access_token, expires_in: v.expires_in };
  if (typeof v.refresh_token === 'string') out.refresh_token = v.refresh_token;
  return out;
}

export async function exchangeCodeForTokens(
  authkitDomain: string,
  clientId: string,
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<TokenResponse> {
  const response = await fetch(`${authkitDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
      client_id: clientId,
    }).toString(),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Token exchange failed (${response.status}): ${body}`);
  }

  return asTokenResponse(await response.json());
}

export class SessionExpiredError extends Error {
  constructor() {
    super('SESSION_EXPIRED');
    this.name = 'SessionExpiredError';
  }
}

export async function refreshAccessToken(
  authkitDomain: string,
  clientId: string,
  refreshToken: string,
): Promise<TokenResponse> {
  const response = await fetch(`${authkitDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    }).toString(),
  });

  if (!response.ok) {
    const status = response.status;
    const body = await response.text();

    if (status === 400 || status === 401) {
      try {
        const parsed = JSON.parse(body) as { error?: string };
        if (parsed.error === 'invalid_grant') throw new SessionExpiredError();
      } catch (err) {
        if (err instanceof SessionExpiredError) throw err;
        // Non-JSON body — fall through to generic error.
      }
    }

    throw new Error(`Token refresh failed (${status}): ${body}`);
  }

  return asTokenResponse(await response.json());
}

export function startCallbackServer(
  port: number,
  expectedState: string,
  timeoutMs: number = 120_000,
): Promise<{ code: string }> {
  return new Promise((resolve, reject) => {
    let handled = false;
    const server = http.createServer((req, res) => {
      const url = new URL(req.url || '/', `http://127.0.0.1:${port}`);
      if (url.pathname !== '/callback') {
        res.writeHead(404);
        res.end();
        return;
      }

      if (handled) {
        res.writeHead(409);
        res.end();
        return;
      }

      const code = url.searchParams.get('code');
      const state = url.searchParams.get('state');
      const error = url.searchParams.get('error');
      const errorDescription = url.searchParams.get('error_description');

      if (error) {
        handled = true;
        res.writeHead(400, { 'Content-Type': 'text/html' });
        res.end(errorPage(errorDescription || error));
        cleanup();
        reject(new Error(`OAuth error: ${errorDescription || error}`));
        return;
      }

      if (state !== expectedState) {
        handled = true;
        res.writeHead(400, { 'Content-Type': 'text/html' });
        res.end(errorPage('Invalid state parameter'));
        cleanup();
        reject(new Error('OAuth state mismatch — possible CSRF attempt'));
        return;
      }

      if (!code) {
        handled = true;
        res.writeHead(400, { 'Content-Type': 'text/html' });
        res.end(errorPage('No authorization code received'));
        cleanup();
        reject(new Error('No authorization code received'));
        return;
      }

      handled = true;
      res.writeHead(200, { 'Content-Type': 'text/html' });
      res.end(successPage());
      cleanup();
      resolve({ code });
    });

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for login. Run `rekor login` again.'));
    }, timeoutMs);

    function cleanup(): void {
      clearTimeout(timeout);
      server.close();
    }

    server.on('error', (err: NodeJS.ErrnoException) => {
      cleanup();
      if (err.code === 'EADDRINUSE') {
        reject(new Error(`Port ${port} is in use. Close the other process and retry.`));
      } else {
        reject(err);
      }
    });

    server.listen(port, '127.0.0.1');
  });
}

function successPage(): string {
  return `<!DOCTYPE html>
<html><head><title>Rekor CLI</title></head>
<body style="font-family: system-ui, sans-serif; display: flex; align-items: center; justify-content: center; min-height: 100vh; margin: 0; background: #fafafa;">
  <div style="text-align: center;">
    <h1 style="font-size: 1.25rem; font-weight: 600;">Authentication successful</h1>
    <p style="color: #666; margin-top: 0.5rem;">You can close this tab and return to your terminal.</p>
  </div>
</body></html>`;
}

// `message` comes from OAuth redirect params — encode so a compromised IdP can't inject HTML into the localhost page.
function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function errorPage(message: string): string {
  return `<!DOCTYPE html>
<html><head><title>Rekor CLI</title></head>
<body style="font-family: system-ui, sans-serif; display: flex; align-items: center; justify-content: center; min-height: 100vh; margin: 0; background: #fafafa;">
  <div style="text-align: center;">
    <h1 style="font-size: 1.25rem; font-weight: 600; color: #dc2626;">Authentication failed</h1>
    <p style="color: #666; margin-top: 0.5rem;">${escapeHtml(message)}</p>
  </div>
</body></html>`;
}
